import { Feature } from "geojson";
import { pick } from "lodash";
import { MapMouseEvent, PointLike } from "mapbox-gl";
import { useEffect } from "react";

import { parseStringifiedProperties } from "../parseStringifiedProperties";

type UseDragMovementProps<T extends Feature = Feature> = {
    map: mapboxgl.Map | null;
    isDragging: boolean;
    draggedFeature: T | null;
    layerIds: string[];
    isDropTarget: (draggedFeature: T, droppedOnFeature: T) => boolean;
    hoverBoundingBoxSize: number;
    onDragMove: (position: [number, number], hoveredFeature: T | null) => void;
};

/**
 * Handles mousemove events during active drag operations
 */
export function useDragMovement<T extends Feature = Feature>({
    map,
    isDragging,
    draggedFeature,
    layerIds,
    isDropTarget,
    hoverBoundingBoxSize,
    onDragMove,
}: UseDragMovementProps<T>) {
    useEffect(() => {
        if (!map || !isDragging) return;

        function handleMouseMove(e: MapMouseEvent) {
            if (!map || !e.point) return;

            // Calculate geo position
            const adjustedPoint = {
                x: e.point.x,
                y: e.point.y,
            };

            const geoPos = map.unproject([adjustedPoint.x, adjustedPoint.y]);
            const geoPosition: [number, number] = [geoPos.lng, geoPos.lat];

            // Query for features at current position to detect potential drop targets
            const bbox: [PointLike, PointLike] = [
                [e.point.x - hoverBoundingBoxSize, e.point.y - hoverBoundingBoxSize],
                [e.point.x + hoverBoundingBoxSize, e.point.y + hoverBoundingBoxSize],
            ];

            const features = map.queryRenderedFeatures(bbox, {
                layers: layerIds,
            });

            // Find valid drop target (exclude the dragged feature itself)
            let hoveredFeature: T | null = null;

            if (draggedFeature) {
                for (const feature of features) {
                    const pickedFeature = pick(feature, ["id", "type", "bbox", "geometry", "properties"]) as T;

                    // Skip the dragged feature itself
                    if ((pickedFeature as any).properties?.id === (draggedFeature as any).properties?.id) {
                        continue;
                    }

                    // Check if it's a valid drop target
                    if (isDropTarget(draggedFeature, pickedFeature)) {
                        hoveredFeature = pickedFeature;

                        if (hoveredFeature.properties) {
                            hoveredFeature.properties = parseStringifiedProperties(hoveredFeature.properties);
                        }

                        break;
                    }
                }
            }

            onDragMove(geoPosition, hoveredFeature);
        }

        map.on("mousemove", handleMouseMove);

        return () => {
            if (!map) return;
            map.off("mousemove", handleMouseMove);
        };
    }, [map, isDragging, draggedFeature, layerIds, isDropTarget, hoverBoundingBoxSize, onDragMove]);
}
